//
// src/pages/Products.js
//
// Tenant-scoped inventory management.
//
// Backend alignment:
// - GET    /api/products
// - POST   /api/products
// - GET    /api/products/{product_id}
// - PUT    /api/products/{product_id}
// - DELETE /api/products/{product_id}
// - PATCH  /api/products/{product_id}/restore
// - POST   /api/products/import_excel
// - GET    /api/products/export-inventory-excel
// - GET    /api/products/download-template
//
// Authentication and X-Tenant-ID are attached centrally
// by src/api.js.
//

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add,
  Archive,
  DeleteForever,
  Download,
  Edit,
  Restore,
  UploadFile,
} from "@mui/icons-material";

import API from "../api";


// =====================================================
// Constants
// =====================================================

const DEFAULT_FORM = {
  productname: "",
  quantity: "",
  price: "",
  barcode: "",
  sku: "",
  is_taxable: true,
  vat_rate: 15,
  is_active: true,
};


const SUPPORTED_IMPORT_EXTENSIONS = [
  ".csv",
  ".xls",
  ".xlsx",
];


// =====================================================
// Helpers
// =====================================================

function getErrorMessage(
  error,
  fallbackMessage
) {
  const detail =
    error?.response?.data?.detail;

  if (
    typeof detail === "string" &&
    detail.trim()
  ) {
    return detail;
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    if (
      typeof detail.message === "string"
    ) {
      return detail.message;
    }

    try {
      return JSON.stringify(detail);
    } catch {
      // Ignore serialization failures.
    }
  }

  if (
    typeof error?.message === "string" &&
    error.message.trim()
  ) {
    return error.message;
  }

  return fallbackMessage;
}


async function getBlobErrorMessage(
  error,
  fallbackMessage
) {
  const data =
    error?.response?.data;

  if (
    data instanceof Blob
  ) {
    try {
      const text =
        await data.text();

      const parsed =
        JSON.parse(text);

      return (
        parsed?.detail ||
        parsed?.message ||
        fallbackMessage
      );
    } catch {
      return fallbackMessage;
    }
  }

  return getErrorMessage(
    error,
    fallbackMessage
  );
}


function normalizeNumber(
  value,
  fallback = 0
) {
  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : fallback;
}


function downloadBlob(
  blobData,
  filename,
  contentType
) {
  const blob =
    blobData instanceof Blob
      ? blobData
      : new Blob(
          [blobData],
          {
            type: contentType,
          }
        );

  const objectUrl =
    window.URL.createObjectURL(
      blob
    );

  const link =
    document.createElement("a");

  link.href =
    objectUrl;

  link.download =
    filename;

  document.body.appendChild(
    link
  );

  link.click();

  link.remove();

  window.URL.revokeObjectURL(
    objectUrl
  );
}


// =====================================================
// Component
// =====================================================

export default function Products() {
  // ===================================================
  // Inventory State
  // ===================================================

  const [
    products,
    setProducts,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");


  // ===================================================
  // Archive State
  // ===================================================

  const [
    includeArchived,
    setIncludeArchived,
  ] = useState(false);


  // ===================================================
  // Product Dialog State
  // ===================================================

  const [
    open,
    setOpen,
  ] = useState(false);


  const [
    editingId,
    setEditingId,
  ] = useState(null);


  const [
    saving,
    setSaving,
  ] = useState(false);


  const [
    form,
    setForm,
  ] = useState(
    DEFAULT_FORM
  );


  // ===================================================
  // Permanent Delete State
  // ===================================================

  const [
    hardDeleteModalOpen,
    setHardDeleteModalOpen,
  ] = useState(false);


  const [
    productToHardDelete,
    setProductToHardDelete,
  ] = useState(null);


  const [
    deleting,
    setDeleting,
  ] = useState(false);


  // ===================================================
  // Import State
  // ===================================================

  const [
    file,
    setFile,
  ] = useState(null);


  const [
    uploading,
    setUploading,
  ] = useState(false);


  const fileInputRef =
    useRef(null);


  // ===================================================
  // Export State
  // ===================================================

  const [
    exporting,
    setExporting,
  ] = useState(false);


  const [
    downloadingTemplate,
    setDownloadingTemplate,
  ] = useState(false);


  // ===================================================
  // Derived State
  // ===================================================

  const activeCount =
    useMemo(
      () =>
        products.filter(
          (product) =>
            product?.is_active !== false
        ).length,
      [products]
    );


  const archivedCount =
    useMemo(
      () =>
        products.filter(
          (product) =>
            product?.is_active === false
        ).length,
      [products]
    );


  // ===================================================
  // Messages
  // ===================================================

  const clearMessages =
    useCallback(() => {
      setError("");
      setSuccessMessage("");
    }, []);


  // ===================================================
  // Fetch Products
  // ===================================================

  const fetchProducts =
    useCallback(
      async () => {
        setLoading(true);
        setError("");

        try {
          const response =
            await API.get(
              "/products",
              {
                params: {
                  include_archived:
                    includeArchived,
                },
              }
            );

          const responseData =
            response.data;

          if (
            Array.isArray(
              responseData
            )
          ) {
            setProducts(
              responseData
            );

            return;
          }

          if (
            Array.isArray(
              responseData?.products
            )
          ) {
            setProducts(
              responseData.products
            );

            return;
          }

          setProducts([]);

        } catch (requestError) {
          console.error(
            "Unable to load inventory:",
            requestError
          );

          setProducts([]);

          setError(
            getErrorMessage(
              requestError,
              "We could not load the inventory. Please try again."
            )
          );

        } finally {
          setLoading(false);
        }
      },
      [
        includeArchived,
      ]
    );


  useEffect(() => {
    fetchProducts();
  }, [
    fetchProducts,
  ]);


  // ===================================================
  // Product Dialog
  // ===================================================

  const handleOpen =
    useCallback(
      (product = null) => {
        clearMessages();

        if (product) {
          setEditingId(
            product.id
          );

          setForm({
            productname:
              product.productname ??
              "",

            quantity:
              product.quantity ??
              "",

            price:
              product.price ??
              "",

            barcode:
              product.barcode ??
              "",

            sku:
              product.sku ??
              product.barcode ??
              "",

            is_taxable:
              product.is_taxable !==
              false,

            vat_rate:
              product.vat_rate ??
              (
                product.is_taxable ===
                false
                  ? 0
                  : 15
              ),

            is_active:
              product.is_active !==
              false,
          });

        } else {
          setEditingId(null);

          setForm({
            ...DEFAULT_FORM,
          });
        }

        setOpen(true);
      },
      [
        clearMessages,
      ]
    );


  const handleClose =
    useCallback(() => {
      if (saving) {
        return;
      }

      setOpen(false);
      setEditingId(null);

      setForm({
        ...DEFAULT_FORM,
      });
    }, [
      saving,
    ]);


  // ===================================================
  // Form Helpers
  // ===================================================

  const updateFormField =
    useCallback(
      (
        field,
        value
      ) => {
        setForm(
          (previous) => ({
            ...previous,
            [field]: value,
          })
        );
      },
      []
    );


  // ===================================================
  // Save Product
  // ===================================================

  const handleSave =
    useCallback(
      async () => {
        clearMessages();

        const productName =
          String(
            form.productname || ""
          ).trim();

        const barcode =
          String(
            form.barcode || ""
          ).trim();

        const sku =
          String(
            form.sku || ""
          ).trim() ||
          barcode;

        const quantity =
          Math.trunc(
            normalizeNumber(
              form.quantity,
              0
            )
          );

        const price =
          normalizeNumber(
            form.price,
            0
          );

        const vatRate =
          form.is_taxable
            ? normalizeNumber(
                form.vat_rate,
                15
              )
            : 0;


        if (!productName) {
          setError(
            "Product name is required."
          );

          return;
        }


        if (!barcode) {
          setError(
            "Barcode is required."
          );

          return;
        }


        if (quantity < 0) {
          setError(
            "Quantity cannot be negative."
          );

          return;
        }


        if (price < 0) {
          setError(
            "Price cannot be negative."
          );

          return;
        }


        if (
          vatRate < 0 ||
          vatRate > 100
        ) {
          setError(
            "VAT rate must be between 0 and 100."
          );

          return;
        }


        const payload = {
          productname:
            productName,

          quantity,

          price,

          barcode,

          sku,

          is_taxable:
            Boolean(
              form.is_taxable
            ),

          vat_rate:
            vatRate,

          is_active:
            Boolean(
              form.is_active
            ),
        };


        setSaving(true);

        try {
          if (editingId) {
            await API.put(
              `/products/${editingId}`,
              payload
            );

            setSuccessMessage(
              "Product updated successfully."
            );

          } else {
            await API.post(
              "/products",
              payload
            );

            setSuccessMessage(
              "Product created successfully."
            );
          }


          setOpen(false);
          setEditingId(null);

          setForm({
            ...DEFAULT_FORM,
          });

          await fetchProducts();

        } catch (requestError) {
          console.error(
            "Unable to save product:",
            requestError
          );

          setError(
            getErrorMessage(
              requestError,
              "We could not save the product. Please review the information and try again."
            )
          );

        } finally {
          setSaving(false);
        }
      },
      [
        clearMessages,
        editingId,
        fetchProducts,
        form,
      ]
    );


  // ===================================================
  // Archive Product
  // ===================================================

  const handleSoftDelete =
    useCallback(
      async (productId) => {
        clearMessages();

        try {
          await API.delete(
            `/products/${productId}`
          );


          setSuccessMessage(
            "Product archived successfully."
          );


          if (
            includeArchived
          ) {
            setProducts(
              (previous) =>
                previous.map(
                  (product) =>
                    product.id ===
                    productId
                      ? {
                          ...product,
                          is_active:
                            false,
                        }
                      : product
                )
            );

          } else {
            setProducts(
              (previous) =>
                previous.filter(
                  (product) =>
                    product.id !==
                    productId
                )
            );
          }

        } catch (requestError) {
          console.error(
            "Unable to archive product:",
            requestError
          );

          setError(
            getErrorMessage(
              requestError,
              "We could not archive the product."
            )
          );
        }
      },
      [
        clearMessages,
        includeArchived,
      ]
    );


  // ===================================================
  // Restore Product
  // ===================================================

  const handleRestore =
    useCallback(
      async (productId) => {
        clearMessages();

        try {
          const response =
            await API.patch(
              `/products/${productId}/restore`,
              {}
            );


          setProducts(
            (previous) =>
              previous.map(
                (product) =>
                  product.id ===
                  productId
                    ? response.data
                    : product
              )
          );


          setSuccessMessage(
            "Product restored successfully."
          );

        } catch (requestError) {
          console.error(
            "Unable to restore product:",
            requestError
          );

          setError(
            getErrorMessage(
              requestError,
              "We could not restore the product."
            )
          );
        }
      },
      [
        clearMessages,
      ]
    );


  // ===================================================
  // Permanent Delete
  // ===================================================

  const requestHardDelete =
    useCallback(
      (product) => {
        clearMessages();

        setProductToHardDelete(
          product
        );

        setHardDeleteModalOpen(
          true
        );
      },
      [
        clearMessages,
      ]
    );


  const closeHardDeleteDialog =
    useCallback(() => {
      if (deleting) {
        return;
      }

      setHardDeleteModalOpen(
        false
      );

      setProductToHardDelete(
        null
      );
    }, [
      deleting,
    ]);


  const handleConfirmHardDelete =
    useCallback(
      async () => {
        if (
          !productToHardDelete
        ) {
          return;
        }


        clearMessages();
        setDeleting(true);


        try {
          await API.delete(
            `/products/${productToHardDelete.id}`,
            {
              params: {
                hard_delete:
                  true,
              },
            }
          );


          setProducts(
            (previous) =>
              previous.filter(
                (product) =>
                  product.id !==
                  productToHardDelete.id
              )
          );


          setSuccessMessage(
            "Product permanently deleted."
          );


          setHardDeleteModalOpen(
            false
          );

          setProductToHardDelete(
            null
          );

        } catch (requestError) {
          console.error(
            "Unable to permanently delete product:",
            requestError
          );

          setError(
            getErrorMessage(
              requestError,
              "This product could not be permanently deleted. It may be referenced by historical sales."
            )
          );

          setHardDeleteModalOpen(
            false
          );

        } finally {
          setDeleting(false);
        }
      },
      [
        clearMessages,
        productToHardDelete,
      ]
    );


  // ===================================================
  // File Selection
  // ===================================================

  const handleFileChange =
    useCallback(
      (event) => {
        clearMessages();

        const selectedFile =
          event.target.files?.[0] ||
          null;


        if (!selectedFile) {
          setFile(null);
          return;
        }


        const lowerName =
          selectedFile.name
            .toLowerCase();


        const supported =
          SUPPORTED_IMPORT_EXTENSIONS.some(
            (extension) =>
              lowerName.endsWith(
                extension
              )
          );


        if (!supported) {
          setFile(null);

          event.target.value =
            "";

          setError(
            "Please select a CSV, XLS, or XLSX inventory file."
          );

          return;
        }


        setFile(
          selectedFile
        );
      },
      [
        clearMessages,
      ]
    );


  // ===================================================
  // Import Inventory
  // ===================================================

  const handleUpload =
    useCallback(
      async () => {
        if (!file) {
          setError(
            "Please select an inventory file first."
          );

          return;
        }


        clearMessages();
        setUploading(true);


        const formData =
          new FormData();

        formData.append(
          "file",
          file
        );


        try {
          // Do not manually set Content-Type.
          // The browser/Axios adds the multipart boundary.
          const response =
            await API.post(
              "/products/import_excel",
              formData
            );


          setSuccessMessage(
            response.data?.message ||
            "Inventory imported successfully."
          );


          setFile(null);


          if (
            fileInputRef.current
          ) {
            fileInputRef.current.value =
              "";
          }


          await fetchProducts();

        } catch (requestError) {
          console.error(
            "Inventory import failed:",
            requestError
          );

          setError(
            getErrorMessage(
              requestError,
              "We could not import the inventory file."
            )
          );

        } finally {
          setUploading(false);
        }
      },
      [
        clearMessages,
        fetchProducts,
        file,
      ]
    );


  // ===================================================
  // Export Inventory
  // ===================================================

  const handleExportExcel =
    useCallback(
      async () => {
        clearMessages();
        setExporting(true);


        try {
          const response =
            await API.get(
              "/products/export-inventory-excel",
              {
                responseType:
                  "blob",
              }
            );


          const contentDisposition =
            response.headers?.[
              "content-disposition"
            ];


          let filename =
            `inventory_export_${
              new Date()
                .toISOString()
                .slice(0, 10)
            }.xlsx`;


          if (
            typeof contentDisposition ===
            "string"
          ) {
            const match =
              contentDisposition.match(
                /filename="?([^"]+)"?/i
              );

            if (match?.[1]) {
              filename =
                match[1];
            }
          }


          downloadBlob(
            response.data,
            filename,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          );


          setSuccessMessage(
            "Inventory export downloaded successfully."
          );

        } catch (requestError) {
          console.error(
            "Inventory export failed:",
            requestError
          );


          const message =
            await getBlobErrorMessage(
              requestError,
              "We could not export the inventory."
            );


          setError(
            message
          );

        } finally {
          setExporting(false);
        }
      },
      [
        clearMessages,
      ]
    );


  // ===================================================
  // Download Import Template
  // ===================================================

  const handleDownloadTemplate =
    useCallback(
      async () => {
        clearMessages();

        setDownloadingTemplate(
          true
        );


        try {
          const response =
            await API.get(
              "/products/download-template",
              {
                responseType:
                  "blob",
              }
            );


          const contentDisposition =
            response.headers?.[
              "content-disposition"
            ];


          let filename =
            "products_template.xlsx";


          if (
            typeof contentDisposition ===
            "string"
          ) {
            const match =
              contentDisposition.match(
                /filename="?([^"]+)"?/i
              );

            if (match?.[1]) {
              filename =
                match[1];
            }
          }


          downloadBlob(
            response.data,
            filename,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          );

        } catch (requestError) {
          console.error(
            "Template download failed:",
            requestError
          );


          const message =
            await getBlobErrorMessage(
              requestError,
              "We could not download the inventory template."
            );


          setError(
            message
          );

        } finally {
          setDownloadingTemplate(
            false
          );
        }
      },
      [
        clearMessages,
      ]
    );


  // ===================================================
  // Render
  // ===================================================

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: 4,
        mb: 4,
      }}
    >
      {/* =================================================
          Header
      ================================================= */}

      <Box
        sx={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          flexWrap:
            "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
          >
            Inventory Management
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
            }}
          >
            Manage products, stock levels,
            taxation and inventory files.
          </Typography>
        </Box>


        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems:
              "center",
            flexWrap:
              "wrap",
          }}
        >
          <Chip
            label={
              `${activeCount} active`
            }
            color="success"
            variant="outlined"
          />

          {includeArchived && (
            <Chip
              label={
                `${archivedCount} archived`
              }
              variant="outlined"
            />
          )}
        </Box>
      </Box>


      {/* =================================================
          Messages
      ================================================= */}

      {error && (
        <Alert
          severity="error"
          onClose={() =>
            setError("")
          }
          sx={{
            mb: 2,
          }}
        >
          {error}
        </Alert>
      )}


      {successMessage && (
        <Alert
          severity="success"
          onClose={() =>
            setSuccessMessage("")
          }
          sx={{
            mb: 2,
          }}
        >
          {successMessage}
        </Alert>
      )}


      {/* =================================================
          Actions
      ================================================= */}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap:
              "wrap",
            alignItems:
              "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={
              <Add />
            }
            onClick={() =>
              handleOpen()
            }
          >
            Add Product
          </Button>


          <FormControlLabel
            control={
              <Switch
                checked={
                  includeArchived
                }
                onChange={
                  (event) =>
                    setIncludeArchived(
                      event.target
                        .checked
                    )
                }
              />
            }
            label="Show archived"
          />


          <Button
            variant="outlined"
            startIcon={
              downloadingTemplate
                ? (
                    <CircularProgress
                      size={18}
                    />
                  )
                : (
                    <Download />
                  )
            }
            onClick={
              handleDownloadTemplate
            }
            disabled={
              downloadingTemplate
            }
          >
            Download Template
          </Button>


          <Button
            variant="outlined"
            startIcon={
              exporting
                ? (
                    <CircularProgress
                      size={18}
                    />
                  )
                : (
                    <Download />
                  )
            }
            onClick={
              handleExportExcel
            }
            disabled={
              exporting ||
              products.length ===
                0
            }
          >
            Export Excel
          </Button>


          <label>
            <input
              ref={
                fileInputRef
              }
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={
                handleFileChange
              }
              hidden
            />

            <Button
              component="span"
              variant="outlined"
              startIcon={
                <UploadFile />
              }
            >
              {file
                ? file.name
                : "Choose File"}
            </Button>
          </label>


          <Button
            variant="contained"
            color="secondary"
            onClick={
              handleUpload
            }
            disabled={
              !file ||
              uploading
            }
            startIcon={
              uploading
                ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                : (
                    <UploadFile />
                  )
            }
          >
            {uploading
              ? "Importing..."
              : "Import Inventory"}
          </Button>
        </Box>
      </Paper>


      {/* =================================================
          Product Table
      ================================================= */}

      <TableContainer
        component={Paper}
        variant="outlined"
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                ID
              </TableCell>

              <TableCell>
                SKU
              </TableCell>

              <TableCell>
                Product
              </TableCell>

              <TableCell
                align="right"
              >
                Quantity
              </TableCell>

              <TableCell
                align="right"
              >
                Price
              </TableCell>

              <TableCell>
                Tax
              </TableCell>

              <TableCell>
                VAT
              </TableCell>

              <TableCell>
                Status
              </TableCell>

              <TableCell>
                Barcode
              </TableCell>

              <TableCell
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>


          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{
                    py: 5,
                  }}
                >
                  <CircularProgress
                    size={30}
                  />
                </TableCell>
              </TableRow>

            ) : products.length ===
              0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{
                    py: 5,
                    color:
                      "text.secondary",
                  }}
                >
                  No products were found.
                </TableCell>
              </TableRow>

            ) : (
              products.map(
                (product) => {
                  const isActive =
                    product.is_active !==
                    false;

                  return (
                    <TableRow
                      key={
                        product.id
                      }
                      hover
                      sx={{
                        opacity:
                          isActive
                            ? 1
                            : 0.6,
                      }}
                    >
                      <TableCell>
                        #{product.id}
                      </TableCell>


                      <TableCell
                        sx={{
                          fontFamily:
                            "monospace",
                        }}
                      >
                        {product.sku ||
                          product.barcode}
                      </TableCell>


                      <TableCell>
                        {product.productname}
                      </TableCell>


                      <TableCell
                        align="right"
                      >
                        {product.quantity}
                      </TableCell>


                      <TableCell
                        align="right"
                      >
                        $
                        {normalizeNumber(
                          product.price
                        ).toFixed(2)}
                      </TableCell>


                      <TableCell>
                        {product.is_taxable
                          ? "Taxable"
                          : "Exempt"}
                      </TableCell>


                      <TableCell>
                        {product.is_taxable
                          ? `${normalizeNumber(
                              product.vat_rate
                            )}%`
                          : "0%"}
                      </TableCell>


                      <TableCell>
                        <Chip
                          label={
                            isActive
                              ? "Active"
                              : "Archived"
                          }
                          color={
                            isActive
                              ? "success"
                              : "default"
                          }
                          size="small"
                          variant={
                            isActive
                              ? "filled"
                              : "outlined"
                          }
                        />
                      </TableCell>


                      <TableCell
                        sx={{
                          fontFamily:
                            "monospace",
                        }}
                      >
                        {product.barcode}
                      </TableCell>


                      <TableCell
                        align="center"
                      >
                        <Tooltip
                          title="Edit product"
                        >
                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleOpen(
                                product
                              )
                            }
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>


                        {isActive ? (
                          <Tooltip
                            title="Archive product"
                          >
                            <IconButton
                              color="warning"
                              onClick={() =>
                                handleSoftDelete(
                                  product.id
                                )
                              }
                            >
                              <Archive />
                            </IconButton>
                          </Tooltip>

                        ) : (
                          <Tooltip
                            title="Restore product"
                          >
                            <IconButton
                              color="success"
                              onClick={() =>
                                handleRestore(
                                  product.id
                                )
                              }
                            >
                              <Restore />
                            </IconButton>
                          </Tooltip>
                        )}


                        <Tooltip
                          title="Permanently delete"
                        >
                          <IconButton
                            color="error"
                            onClick={() =>
                              requestHardDelete(
                                product
                              )
                            }
                          >
                            <DeleteForever />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {/* =================================================
          Create / Edit Dialog
      ================================================= */}

      <Dialog
        open={open}
        onClose={
          handleClose
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingId
            ? "Edit Product"
            : "Add Product"}
        </DialogTitle>


        <DialogContent
          sx={{
            display: "flex",
            flexDirection:
              "column",
            gap: 2.5,
            pt: 2,
          }}
        >
          <TextField
            label="Product Name"
            value={
              form.productname
            }
            onChange={
              (event) =>
                updateFormField(
                  "productname",
                  event.target.value
                )
            }
            fullWidth
            required
          />


          <TextField
            label="Barcode"
            value={
              form.barcode
            }
            onChange={
              (event) => {
                const value =
                  event.target.value;

                setForm(
                  (previous) => ({
                    ...previous,

                    barcode:
                      value,

                    sku:
                      (
                        !previous.sku ||
                        previous.sku ===
                          previous.barcode
                      )
                        ? value
                        : previous.sku,
                  })
                );
              }
            }
            fullWidth
            required
          />


          <TextField
            label="SKU"
            value={
              form.sku
            }
            onChange={
              (event) =>
                updateFormField(
                  "sku",
                  event.target.value
                )
            }
            helperText="Leave blank to use the barcode."
            fullWidth
          />


          <TextField
            label="Quantity"
            type="number"
            value={
              form.quantity
            }
            onChange={
              (event) =>
                updateFormField(
                  "quantity",
                  event.target.value
                )
            }
            inputProps={{
              min: 0,
              step: 1,
            }}
            fullWidth
            required
          />


          <TextField
            label="Retail Price"
            type="number"
            value={
              form.price
            }
            onChange={
              (event) =>
                updateFormField(
                  "price",
                  event.target.value
                )
            }
            inputProps={{
              min: 0,
              step: "0.01",
            }}
            fullWidth
            required
          />


          <FormControlLabel
            control={
              <Checkbox
                checked={
                  form.is_taxable
                }
                onChange={
                  (event) => {
                    const taxable =
                      event.target
                        .checked;

                    setForm(
                      (previous) => ({
                        ...previous,

                        is_taxable:
                          taxable,

                        vat_rate:
                          taxable
                            ? (
                                previous.vat_rate ||
                                15
                              )
                            : 0,
                      })
                    );
                  }
                }
              />
            }
            label="Product attracts VAT"
          />


          <TextField
            select
            label="VAT Rate"
            value={
              form.vat_rate
            }
            onChange={
              (event) =>
                updateFormField(
                  "vat_rate",
                  Number(
                    event.target.value
                  )
                )
            }
            disabled={
              !form.is_taxable
            }
            fullWidth
          >
            <MenuItem
              value={15}
            >
              Standard Rate (15%)
            </MenuItem>

            <MenuItem
              value={5}
            >
              Concessionary Rate (5%)
            </MenuItem>

            <MenuItem
              value={0}
            >
              Zero Rated (0%)
            </MenuItem>
          </TextField>
        </DialogContent>


        <DialogActions>
          <Button
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            Cancel
          </Button>

          <Button
            onClick={
              handleSave
            }
            variant="contained"
            disabled={
              saving
            }
            startIcon={
              saving
                ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                : null
            }
          >
            {saving
              ? "Saving..."
              : (
                  editingId
                    ? "Update Product"
                    : "Save Product"
                )}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          Permanent Delete Confirmation
      ================================================= */}

      <Dialog
        open={
          hardDeleteModalOpen
        }
        onClose={
          closeHardDeleteDialog
        }
      >
        <DialogTitle
          color="error"
        >
          Permanently Delete Product
        </DialogTitle>


        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently
            delete{" "}
            <strong>
              {
                productToHardDelete
                  ?.productname
              }
            </strong>
            ?
            <br />
            <br />
            Permanent deletion cannot be undone.
            Products referenced by historical sales
            may be protected from deletion by the
            database. Archiving is recommended in
            those cases.
          </DialogContentText>
        </DialogContent>


        <DialogActions>
          <Button
            onClick={
              closeHardDeleteDialog
            }
            disabled={
              deleting
            }
          >
            Cancel
          </Button>

          <Button
            onClick={
              handleConfirmHardDelete
            }
            color="error"
            variant="contained"
            disabled={
              deleting
            }
            startIcon={
              deleting
                ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                : (
                    <DeleteForever />
                  )
            }
          >
            {deleting
              ? "Deleting..."
              : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}